/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 *
 * ArceeRealtimeClient — WebSocket connection manager.
 * (ARCEE-ENH-008 — Real-Time Infrastructure)
 *
 * Responsibilities:
 *  - Opens a persistent WebSocket to ws(s)://host/ws/app/{orgId}/{appId}/
 *  - Provides subscribe(eventType, handler) / unsubscribe pattern
 *  - Auto-reconnects with exponential backoff on unexpected close
 *  - Sends subscribe / unsubscribe messages to backend consumer
 *  - Graceful disconnect (stops reconnect loop)
 *
 * Usage (inside NotificationContext):
 *   const client = new ArceeRealtimeClient(orgId, appId, wsBaseUrl);
 *   const unsub = client.subscribe('notification', (data) => { ... });
 *   client.connect();
 *   // later:
 *   unsub();
 *   client.disconnect();
 */

class ArceeRealtimeClient {
  /**
   * @param {string|number} orgId      - Organization ID
   * @param {string|number} appId      - Application ID
   * @param {string}        wsBaseUrl  - e.g. "ws://localhost:8000"
   */
  /**
   * @param {string|number} orgId      - Organization ID
   * @param {string|number} appId      - Application ID
   * @param {string}        wsBaseUrl  - e.g. "ws://localhost:8000"
   * @param {string}        token      - OAuth2 access token
   */
  constructor(orgId, appId, wsBaseUrl, token) {
    const base = `${wsBaseUrl}/ws/app/${orgId}/${appId}/`;
    this.url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
    this.subscriptions = new Map();   // eventType → handler[]
    this.ws = null;
    this._active = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
  }

  /**
   * Open the WebSocket connection.
   * Call once after creating the client.
   */
  connect() {
    this._active = true;
    this._open();
  }

  /**
   * Subscribe to a specific event type.
   * Multiple handlers per type are supported.
   *
   * @param   {string}   eventType  - e.g. 'notification', 'record_created'
   * @param   {Function} handler    - called with parsed JSON payload
   * @returns {Function}            - call to unsubscribe this handler
   */
  subscribe(eventType, handler) {
    if (!this.subscriptions.has(eventType)) {
      this.subscriptions.set(eventType, []);
    }
    this.subscriptions.get(eventType).push(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.subscriptions.get(eventType) || [];
      this.subscriptions.set(eventType, handlers.filter(h => h !== handler));
    };
  }

  /**
   * Ask the backend consumer to subscribe to a named sub-channel.
   * Used for future app-wide record feeds (records_orders, etc.)
   */
  subscribeChannel(channel) {
    this._send({ type: 'subscribe', channel });
  }

  /**
   * Ask the backend consumer to unsubscribe from a named sub-channel.
   */
  unsubscribeChannel(channel) {
    this._send({ type: 'unsubscribe', channel });
  }

  /**
   * Close the WebSocket and stop reconnect attempts.
   */
  disconnect() {
    this._active = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // ── Private ────────────────────────────────────────────────────────────

  _open() {
    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      console.warn('[ArceeWS] Failed to construct WebSocket:', e);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      console.debug(`[ArceeWS] Connected to ${this.url}`);
    };

    this.ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.warn('[ArceeWS] Could not parse message:', event.data);
        return;
      }

      const handlers = this.subscriptions.get(data.type) || [];
      handlers.forEach(handler => {
        try { handler(data); }
        catch (e) { console.error('[ArceeWS] Handler error:', e); }
      });
    };

    this.ws.onclose = (event) => {
      console.debug(`[ArceeWS] Closed — code=${event.code} wasClean=${event.wasClean}`);
      // 4001 = unauthenticated, 4003 = not a member — do not reconnect
      if (this._active && event.code !== 4001 && event.code !== 4003) {
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onclose fires right after — reconnect handled there
      console.warn('[ArceeWS] Connection error');
    };
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[ArceeWS] Max reconnect attempts reached. Giving up.');
      return;
    }
    // Exponential backoff: 1s, 2s, 4s, 8s, … capped at 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    console.debug(`[ArceeWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => {
      if (this._active) this._open();
    }, delay);
  }

  _send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}

export default ArceeRealtimeClient;
